import React, { FunctionComponent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { View, ViewStyle } from 'react-native'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { CircleBadge } from '../../atoms/circleBadge/CircleBadge'
import { DropdownMenu, MENU_SELECTED_ITEM_STYLE } from '../../atoms/formElements/typeAhead/DropdownMenu'
import {
  TypeAheadCustomOptionsConfig,
  ValueFetchMode,
} from '../../atoms/formElements/typeAhead/TypeAheadCustomOptionsConfig'
import { HamburgerMenuIcon } from '../../atoms/icons/HamburgerMenuIcon'
import { XIcon } from '../../atoms/icons/XIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { IS_WEB } from '../../constants'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import { BottomSheet } from '../../molecules/bottomSheet/BottomSheet'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { AuroraIconCircle } from '../lightHomeToolbar/utils'

export interface MenuItemProps {
  text: string
  customTextComponent?: ReactNode
  id: string
  testId?: string
  onPressItem: () => void
  icon?: React.ReactElement | ((isSelected: boolean) => React.ReactElement)
  showBadge?: boolean
  disabled?: boolean
  itemStyle?: ViewStyle
}

export interface MenuProps {
  menuItems: MenuItemProps[]
  testID?: string
  dropdownStyle?: ViewStyle
  menuItemStyle?: ViewStyle
  preselectedValue?: string
  dropdownWidth?: string | number
  horizontalPosition?: 'right' | 'left'
  disableSelect?: boolean
  showIcons?: boolean
  disableBottomSheetForWeb?: boolean
  trackMenuClick?: () => void
  showMenuBadge?: boolean
  animateBadge?: boolean
  /** Add hover/press effects and padding */
  highlightTrigger?: boolean
  pressableTriggerStyle?: (pressed: boolean, focusVisible: boolean) => ViewStyle
  customMenuIcon?: ReactNode
  disabled?: boolean
  onPressHandler?: (selected: string) => void
}
const MenuContainer = styled.View<{ positionRight?: boolean }>(({ positionRight }) => ({
  alignItems: positionRight ? 'flex-end' : 'flex-start',
}))

const MenuDropdownContainer = styled.View<{ theme: ThemeType; width?: string | number; positionRight?: boolean }>(
  ({ theme, width, positionRight }) => ({
    position: 'absolute',
    backgroundColor: theme.colors.backgroundPrimary,
    padding: `${theme.spacing['12px']} 0`,
    borderRadius: theme.spacing['8px'],
    boxShadow: `0 2px 16px ${theme.colors.shadowLow}`,
    ...(width && { width: width }),
    top: '100%',
    ...(positionRight ? { right: 0 } : { left: 0 }),
  }),
)

const BottomSheetMenuContainer = styled.View<{
  paddingTop: number
  paddingBottom: number
}>(({ paddingTop, paddingBottom }) => ({
  padding: `${paddingTop}px 0 ${paddingBottom}px 0`,
}))

const IconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['12px'],
}))

const MenuBadge = styled.View({
  position: 'absolute',
  top: '-2px',
  right: '-2px',
})

const MenuItemBadge = styled.View({
  marginLeft: '6px',
  alignSelf: 'center',
})

const MenuTriggerContainer = styled(PressableOpacity)<{ theme: ThemeType; highlightTrigger: boolean }>(
  ({ theme, highlightTrigger }) => ({
    ...(highlightTrigger && {
      padding: theme.isAuroraTheme ? theme.spacing['12px'] : theme.spacing['8px'],
      borderRadius: theme.isAuroraTheme ? '24px' : theme.spacing['8px'],
    }),
  }),
)

export const Menu: FunctionComponent<MenuProps> = ({
  menuItems,
  dropdownStyle,
  menuItemStyle,
  testID = 'Menu',
  preselectedValue,
  dropdownWidth = 206,
  horizontalPosition = 'left',
  disableSelect = true,
  showIcons = false,
  disableBottomSheetForWeb = false,
  trackMenuClick = noop,
  showMenuBadge = false,
  animateBadge = false,
  highlightTrigger = false,
  pressableTriggerStyle,
  customMenuIcon,
  disabled = false,
  onPressHandler,
}) => {
  const { formatMessage } = useIntl()
  const menuDropdownRef = useRef(null)
  const bottomSheetRef = useRef<BottomSheetGorhom>(null)
  const [selectedValue, setSelectedValue] = useState(preselectedValue)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const { breakpoints, colors, isAuroraTheme } = useTheme() as ThemeType
  const menuDropdownRefId = `${testID}-menuDropdown`
  const menuTriggerRefId = `${testID}-menuTrigger`
  const useBottomSheet = !IS_WEB || (!disableBottomSheetForWeb && breakpoints.isMobileSized)

  const bottomSheetMenuTopPadding = 24
  const bottomSheetMenuBottomPadding = 48
  const bottomSheetMenuItemStyle = { paddingVertical: 16, paddingHorizontal: 24 }

  useOnClickOutside(
    menuDropdownRefId,
    menuDropdownRef,
    () => {
      if (!useBottomSheet) {
        setIsDropdownOpen(false)
      }
    },
    menuTriggerRefId,
  )

  useEffect(() => {
    setSelectedValue(preselectedValue)
  }, [preselectedValue])

  const menuItemRenderer = (item: MenuItemProps, displayText: JSX.Element, isSelected?: boolean) => (
    <>
      {item.icon && showIcons && (
        <IconContainer>{typeof item.icon === 'function' ? item.icon(isSelected ?? false) : item.icon}</IconContainer>
      )}
      {/* if `customTextComponent` is passed, render it; otherwise fall back to the string */}
      {item.customTextComponent ?? displayText}
      {item.showBadge && (
        <MenuItemBadge>
          <CircleBadge />
        </MenuItemBadge>
      )}
    </>
  )

  const handlePress = (selected: string) => {
    if (onPressHandler) {
      onPressHandler(selected)
    }
    if (!disableSelect) {
      setSelectedValue(selected)
    }
    bottomSheetRef?.current?.close()
    menuItems.find((option) => option.text === selected)?.onPressItem()
    IS_WEB && setIsDropdownOpen((prev) => !prev)
  }

  const dropdown = (
    <DropdownMenu
      options={menuItems.map((option) => option.text)}
      onOptionPress={(selected) => handlePress(selected)}
      value={selectedValue ? [selectedValue] : ['']}
      selectedItemStyle={MENU_SELECTED_ITEM_STYLE.LEFT_BORDER}
      getMenuItemStyle={({ text }) => {
        const item = menuItems.find((option) => option.text === text)
        return { ...menuItemStyle, ...(useBottomSheet && bottomSheetMenuItemStyle), ...item?.itemStyle }
      }}
      customOptionsConfig={
        new TypeAheadCustomOptionsConfig(
          menuItems,
          menuItemRenderer,
          undefined,
          'text',
          undefined,
          ValueFetchMode.LEGACY_LABEL,
        )
      }
      customOptions={menuItems}
    />
  )

  const closeBottomSheet = useCallback(() => {
    bottomSheetRef?.current?.close()
    setIsDropdownOpen(false)
  }, [])

  const mainMenuChildren = () => {
    return (
      <MenuContainer testID={tID(testID)} positionRight={horizontalPosition === 'right'}>
        <MenuTriggerContainer
          {...(highlightTrigger && {
            hoveredBackgroundColor: colors.tertiaryButtonBackgroundHover,
            pressedBackgroundColor: colors.tertiaryButtonBackgroundPressed,
          })}
          testID={tID(menuTriggerRefId)}
          nativeID={menuTriggerRefId}
          onPress={() => {
            if (!disabled) {
              setIsDropdownOpen((prev) => !prev)
              trackMenuClick()
            }
          }}
          highlightTrigger={highlightTrigger}
          styleByState={pressableTriggerStyle}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Menu',
            description: 'Button that opens a menu',
          })}
          disabled={disabled}
        >
          {customMenuIcon ? (
            customMenuIcon
          ) : isDropdownOpen && IS_WEB ? (
            <XIcon />
          ) : (
            <View>
              <HamburgerMenuIcon />
              {showMenuBadge && (
                <MenuBadge>
                  <CircleBadge animate={animateBadge} />
                </MenuBadge>
              )}
            </View>
          )}
        </MenuTriggerContainer>
        {!useBottomSheet && isDropdownOpen && (
          <MenuDropdownContainer
            testID={tID(`${testID}-menuDropdown`)}
            width={dropdownWidth}
            ref={menuDropdownRef}
            nativeID={menuDropdownRefId}
            style={dropdownStyle}
            positionRight={horizontalPosition === 'right'}
          >
            {dropdown}
          </MenuDropdownContainer>
        )}
        {useBottomSheet && isDropdownOpen && (
          <BottomSheet
            ref={bottomSheetRef}
            onCloseEnd={() => {
              closeBottomSheet()
            }}
            enableScroll
            useWebPortal
            withPortal
            useDynamicHeight
          >
            <BottomSheetMenuContainer
              paddingTop={bottomSheetMenuTopPadding}
              paddingBottom={bottomSheetMenuBottomPadding}
            >
              {dropdown}
            </BottomSheetMenuContainer>
          </BottomSheet>
        )}
      </MenuContainer>
    )
  }

  return isAuroraTheme ? <AuroraIconCircle>{mainMenuChildren()}</AuroraIconCircle> : mainMenuChildren()
}
